import { createContext, useContext } from 'react';

import { DynamicFieldWrapper, DynamicFormErrorMessage } from './dynamic-field';
import { AnyDynamicFormComponentRegistry } from './field-registry';
import { DynamicFormItemsRender } from './items-render';
import { DynamicFieldStateInput } from './types';

/** What a nested DynamicField (a section's questions) renders with: spread it onto the field. */
export interface DynamicFormContextType<
  TRegistry extends AnyDynamicFormComponentRegistry = AnyDynamicFormComponentRegistry,
  TWrapperProps extends object = object,
> {
  wrapper: DynamicFieldWrapper<TRegistry, TWrapperProps> | undefined;
  wrapperProps: TWrapperProps | undefined;
  fieldState: DynamicFieldStateInput | undefined;
  errorMessage: DynamicFormErrorMessage<TRegistry> | undefined;
  componentRegistry: TRegistry;
  itemsRender: DynamicFormItemsRender<TRegistry>;
}

export const DynamicFormContext = createContext<DynamicFormContextType | undefined>(undefined);

export function useDynamicFormContext<
  TRegistry extends AnyDynamicFormComponentRegistry = AnyDynamicFormComponentRegistry,
  TWrapperProps extends object = object,
>() {
  const context = useContext(DynamicFormContext) as
    | DynamicFormContextType<TRegistry, TWrapperProps>
    | undefined;

  if (!context) {
    throw new Error('useDynamicFormContext must be used within a Dynamic Form');
  }

  return context;
}
